export const DEFAULT_CATERING_ALGORITHM = {
  basePrice: 500,
  includedPeople: 30,
  pricePerExtraGuest: 4,
  includedHours: 2,
  pricePerExtraHour: 75,
  staffFee: 150,
  includedFlavors: 4,
  pricePerExtraFlavor: 35,
  minPeople: 30,
  maxPeople: 150,
  minHours: 2,
  maxHours: 12,
  minFlavors: 2,
  maxFlavors: 8,
  description:
    "Plan gelato, wood fired pizza, panini, salads, and cakes for Phoenix events. Final pricing is confirmed after the team reviews event details."
};

function numeric(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function integer(value, fallback) {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) ? number : fallback;
}

export function normalizeCateringAlgorithm(input = {}) {
  const merged = {
    ...DEFAULT_CATERING_ALGORITHM,
    ...(input || {})
  };

  return {
    basePrice: numeric(merged.basePrice, DEFAULT_CATERING_ALGORITHM.basePrice),
    includedPeople: integer(merged.includedPeople, DEFAULT_CATERING_ALGORITHM.includedPeople),
    pricePerExtraGuest: numeric(merged.pricePerExtraGuest, DEFAULT_CATERING_ALGORITHM.pricePerExtraGuest),
    includedHours: integer(merged.includedHours, DEFAULT_CATERING_ALGORITHM.includedHours),
    pricePerExtraHour: numeric(merged.pricePerExtraHour, DEFAULT_CATERING_ALGORITHM.pricePerExtraHour),
    staffFee: numeric(merged.staffFee, DEFAULT_CATERING_ALGORITHM.staffFee),
    includedFlavors: integer(merged.includedFlavors, DEFAULT_CATERING_ALGORITHM.includedFlavors),
    pricePerExtraFlavor: numeric(merged.pricePerExtraFlavor, DEFAULT_CATERING_ALGORITHM.pricePerExtraFlavor),
    minPeople: integer(merged.minPeople, DEFAULT_CATERING_ALGORITHM.minPeople),
    maxPeople: integer(merged.maxPeople, DEFAULT_CATERING_ALGORITHM.maxPeople),
    minHours: integer(merged.minHours, DEFAULT_CATERING_ALGORITHM.minHours),
    maxHours: integer(merged.maxHours, DEFAULT_CATERING_ALGORITHM.maxHours),
    minFlavors: integer(merged.minFlavors, DEFAULT_CATERING_ALGORITHM.minFlavors),
    maxFlavors: integer(merged.maxFlavors, DEFAULT_CATERING_ALGORITHM.maxFlavors),
    description: merged.description || DEFAULT_CATERING_ALGORITHM.description
  };
}

export function createDefaultCateringQuote(algorithm = DEFAULT_CATERING_ALGORITHM) {
  const normalized = normalizeCateringAlgorithm(algorithm);

  return {
    people: normalized.includedPeople,
    duration: normalized.includedHours,
    staffed: false,
    flavors: normalized.includedFlavors
  };
}

export function clampCateringQuote(quote, algorithm = DEFAULT_CATERING_ALGORITHM) {
  const normalized = normalizeCateringAlgorithm(algorithm);

  return {
    people: Math.min(normalized.maxPeople, Math.max(normalized.minPeople, Number(quote.people) || normalized.includedPeople)),
    duration: Math.min(normalized.maxHours, Math.max(normalized.minHours, Number(quote.duration) || normalized.includedHours)),
    staffed: Boolean(quote.staffed),
    flavors: Math.min(normalized.maxFlavors, Math.max(normalized.minFlavors, Number(quote.flavors) || normalized.includedFlavors))
  };
}

export function calculateCateringEstimate(quote, algorithm = DEFAULT_CATERING_ALGORITHM) {
  const normalized = normalizeCateringAlgorithm(algorithm);
  const safeQuote = clampCateringQuote(quote, normalized);
  const peopleCost = Math.max(0, safeQuote.people - normalized.includedPeople) * normalized.pricePerExtraGuest;
  const durationCost = Math.max(0, safeQuote.duration - normalized.includedHours) * normalized.pricePerExtraHour;
  const staffCost = safeQuote.staffed ? normalized.staffFee : 0;
  const flavorCost = Math.max(0, safeQuote.flavors - normalized.includedFlavors) * normalized.pricePerExtraFlavor;

  return Math.round(normalized.basePrice + peopleCost + durationCost + staffCost + flavorCost);
}
